import { notification } from 'antd'
import i18next from 'i18next'
import { action, makeAutoObservable } from 'mobx'
import { captureException } from '@sentry/react'

import { FileEntityType } from 'services/FileService'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { getActualUrls } from 'sections/LKSection/shared/PickPointSharedModule/utils'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { EventType } from 'types/types'

import { ordersApi } from '../../api'
import { OrderKeys } from '../../data'
import { GetTariffsRequest, OrderStatus } from '../../types'
import { CreateOrderUiStore } from './CreateOrderUiStore'

const getKey = (key: string) => `${OrderKeys.createOrderPage}.api.${key}`

export class CreateOrderApiStore {
  constructor(protected readonly uiStore: CreateOrderUiStore) {
    makeAutoObservable(this)
  }

  @action
  createOrder = async () => {
    this.uiStore.setIsLoading(true)

    let orderId: number = 0
    const location = this.uiStore.location

    if (!this.uiStore.hasErrorWithFiles) {
      try {
        if (location.cityItem) {
          const res = await ordersApi.createOrder({
            actualUrls: '',
            area: +(this.uiStore.workConditions.area || 0),
            warehouseArea: +(this.uiStore.workConditions.warehouseArea || 0),
            cellingHeight: +(this.uiStore.workConditions.cellingHeight || 0),
            dressingRoomsCount: +(this.uiStore.workConditions.dressingRoomsCount || 0),
            phone: this.uiStore.getUserPhone,
            timetable: JSON.stringify(
              this.uiStore.workConditions.timetable.getTimetable(
                location.pickPointType ?? PickPointType.partner,
                location.floorLevel,
              ),
            ),
            exPickPointId: this.uiStore.exPickPointId || undefined,
            locations: [location.floorLevel, location.entrance],
            description: location.description,
            courierWay: location.courierWay,
            accessType: location.couriersAccess,
            cityId: location.cityItem.id,
            cityName: location.cityItem.prefix,
            country: this.uiStore.companyCountry,
            district: location.cityItem.name,
            house: location.house,
            latitude: location.coordinates.latitude,
            longitude: location.coordinates.longitude,
            region: location.cityItem.formattedAddress.split(', ')[1],
            street: location.street,
            pickPointType: location.pickPointType || PickPointType.none,
            subsidyHexType: location.subsidyHexTypeItem?.value,
          })

          orderId = res.data
        } else {
          throw new Error()
        }
      } catch (error) {
        notification.error({
          message: i18next.t(getSharedKey('api.createOrderError')),
          description: (error as Error).message,
        })
        sendEvent(EventType.orderStep, {
          step: 4,
          success: 0,
          reason: `Ошибка создания заявки: ${(error as Error).message}`,
        })
        captureException(`Ошибка создания заявки: ${(error as Error).message}`)

        return false
      } finally {
        this.uiStore.setIsLoading(false)
      }
    }

    const videos = this.uiStore.fileList.videos.filter((video) => video?.url)
    const photos = this.uiStore.fileList.photos.filter((photo) => photo?.url)
    const documents = this.uiStore.fileList.documents.filter((document) => document?.url)

    let actualUrls: string | null = ''

    try {
      const [savedVideos, savedPhotos, savedDocuments] = await Promise.all([
        this.uiStore.fileList.uploadVideo(orderId, videos, FileEntityType.pickPointOrder),
        this.uiStore.fileList.uploadPhotos(orderId, photos, FileEntityType.pickPointOrder),
        this.uiStore.fileList.uploadPhotos(orderId, documents, FileEntityType.pickPointOrder, true),
      ])

      actualUrls = getActualUrls(savedPhotos, savedVideos, savedDocuments)
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('fileUploadError')),
        description: (error as Error).message,
      })

      sendEvent(EventType.orderStep, {
        step: 4,
        success: 0,
        reason: `Ошибка загрузки файлов для заявки: ${(error as Error).message}`,
      })
      captureException(`Ошибка загрузки файлов для заявки № ${orderId}: ${(error as Error).message}`)
      this.uiStore.setHasErrorWithFiles(true)

      return false
    }

    try {
      if (actualUrls) {
        await this.uiStore.orderStore.updateOrder({
          id: orderId,
          actualUrls,
        })

        notification.success({ message: i18next.t(getKey('createOrderSuccess'), { orderId }) })
        this.uiStore.setHasErrorWithFiles(false)

        return orderId
      } else {
        return false
      }
    } catch (error) {
      notification.error({
        message: i18next.t(getKey('addFilesToOrderError')),
        description: (error as Error).message,
      })

      sendEvent(EventType.orderStep, {
        step: 4,
        success: 0,
        reason: `Ошибка добавления файлов к заявке: ${(error as Error).message}`,
      })
      captureException(`Ошибка добавления файлов к заявке № ${orderId}: ${(error as Error).message}`)
      this.uiStore.setHasErrorWithFiles(true)

      return false
    } finally {
      this.uiStore.setIsLoading(false)
    }
  }

  async getTariffs({ area, lat, lon, typePoint }: GetTariffsRequest) {
    try {
      const result = await ordersApi.getTariffs({
        area,
        lat,
        lon,
        typePoint,
      })

      let tariff = ''

      if (result) {
        this.uiStore.setTariff(result.tariff)

        try {
          tariff = JSON.parse(result.tariffDescription)
        } catch {
          tariff = result.tariffDescription
        }
      } else {
        notification.error({
          message: i18next.t(getKey('calculateTariffError')),
        })
      }

      return tariff
    } catch (error) {
      notification.error({ message: i18next.t(getKey('getTariffError')), description: (error as Error).message })
    }
  }

  @action
  async publishOrder(orderId: number) {
    try {
      const result = await ordersApi.publishOrder(orderId)

      this.uiStore.setOrderId(null)

      if (result.status === OrderStatus.created) {
        notification.success({
          message: i18next.t(getKey('publishOrderSuccess')),
        })
      }

      this.uiStore.rootStore.orderStore.loadList()

      return result
    } catch (error) {
      this.uiStore.setOrderId(null)
      notification.error({
        message: i18next.t(getKey('publishOrderError')),
        description: (error as Error).message,
      })
    }
  }

  @action
  async checkOrderAddress() {
    const location = this.uiStore.location

    try {
      if (location.cityItem) {
        const result = await ordersApi.checkOrderAddress({
          cityId: location.cityItem.id.toString(),
          cityName: location.cityItem.prefix,
          country: this.uiStore.companyCountry,
          house: location.house,
          region: location.cityItem.formattedAddress.split(', ')[1],
          street: location.street,
          coordinates: {
            latitude: +location.coordinates.latitude,
            longitude: +location.coordinates.longitude,
          },
        })

        return result
      }
    } catch (error) {
      sendEvent(EventType.orderStep, {
        step: 2,
        success: 0,
        reason: `Выбранный адрес не подходит: ${(error as Error).message}`,
      })

      notification.error({
        message: i18next.t(getKey('checkOrderAddressError')),
        description: (error as Error).message,
      })
      return false
    }
  }
}
